<template>
  <div class="proj-card">
    <div class="proj-card__header">
      <span class="proj-card__title">Proyección y riesgo financiero</span>
      <span class="proj-card__subtitle">Próximos 3 meses</span>
    </div>

    <div v-if="needsAttention" class="proj-card__warning">
      <span class="mdi mdi-alert"></span>
      Más del 15% de los ingresos proyectados están en riesgo de no renovarse.
    </div>

    <div class="proj-card__months">
      <div v-for="mo in months" :key="`${mo.year}-${mo.month}`" class="proj-month" :class="`proj-month--${mo.riskLevel?.toLowerCase()}`">
        <div class="proj-month__header">
          <span class="proj-month__label">{{ mo.label }} {{ mo.year }}</span>
          <span class="proj-month__dot" :class="`proj-month__dot--${mo.riskLevel?.toLowerCase()}`"></span>
        </div>

        <div class="proj-month__bar">
          <div
            class="proj-month__bar-segment proj-month__bar-segment--safe"
            :style="{ width: barWidth(mo.safeAmount, mo.projectedTotal) }"
          ></div>
          <div
            class="proj-month__bar-segment proj-month__bar-segment--risk"
            :style="{ width: barWidth(mo.atRiskAmount, mo.projectedTotal) }"
          ></div>
        </div>

        <span class="proj-month__total">{{ formatCurrency(mo.projectedTotal) }}</span>
        <span class="proj-month__detail">{{ mo.detailText }}</span>

        <div class="proj-month__risks">
          <div v-if="!mo.risks || mo.risks.length === 0" class="proj-month__no-risk">
            Sin riesgos identificados
          </div>
          <template v-else>
            <div v-for="r in mo.risks" :key="r.key" class="proj-month__risk-row" :class="`proj-month__risk-row--${r.severity.toLowerCase()}`">
              <span class="proj-month__risk-dot" :class="`proj-month__risk-dot--${r.severity.toLowerCase()}`"></span>
              <span class="proj-month__risk-concept">{{ r.concept }}</span>
              <span class="proj-month__risk-amount">{{ formatCompact(r.amount) }}</span>
            </div>
            <div v-if="mo.extraRisksCount > 0" class="proj-month__risk-more">
              +{{ mo.extraRisksCount }} más
            </div>
          </template>
        </div>
      </div>
    </div>

    <div class="proj-card__footer">
      <div class="proj-card__footer-item">
        <span class="proj-card__footer-label">Total proyectado (3 meses)</span>
        <span class="proj-card__footer-value">{{ formatCurrency(projectedTotalSum) }}</span>
      </div>
      <div class="proj-card__footer-item">
        <span class="proj-card__footer-label">En riesgo</span>
        <span class="proj-card__footer-value proj-card__footer-value--risk">
          {{ formatCurrency(riskTotalSum) }} ({{ (riskRatio * 100).toFixed(0) }}%)
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
defineProps({
  months: { type: Array, default: () => [] },
  projectedTotalSum: { type: Number, default: 0 },
  riskTotalSum: { type: Number, default: 0 },
  riskRatio: { type: Number, default: 0 },
  needsAttention: { type: Boolean, default: false },
})

function barWidth(amount, total) {
  if (!total) return '0%'
  return `${Math.min(100, Math.max(0, (amount / total) * 100))}%`
}

function formatCurrency(amount) {
  if (amount === undefined || amount === null) return '—'
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(amount)
}

function formatCompact(amount) {
  if (amount === undefined || amount === null) return '—'
  const abs = Math.abs(amount)
  if (abs >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`
  if (abs >= 1000) return `$${Math.round(amount / 1000)}K`
  return formatCurrency(amount)
}
</script>

<style scoped>
.proj-card {
  background: var(--bg-surface-1);
  border: 1px solid var(--border-active);
  border-radius: 8px;
  padding: 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.proj-card__header {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.7rem;
}

.proj-card__title {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.95rem;
  color: var(--text-content);
}

.proj-card__subtitle {
  font-family: var(--font-body);
  font-size: 0.72rem;
  color: #94a3b8;
}

.proj-card__warning {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: #fef3c7;
  border: 1px solid #fde68a;
  color: #92400e;
  border-radius: 6px;
  padding: 0.5rem 0.7rem;
  font-family: var(--font-body);
  font-size: 0.78rem;
  margin-bottom: 0.8rem;
}

.proj-card__months {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.8rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  align-content: start;
}

.proj-card__months::-webkit-scrollbar {
  width: 9px;
}

.proj-card__months::-webkit-scrollbar-thumb {
  background: var(--border-subtle);
  border-radius: 3px;
}

.proj-card__months::-webkit-scrollbar-track {
  background: transparent;
}

.proj-month {
  display: flex;
  flex-direction: column;
  border: 1px solid #f1f5f9;
  border-radius: 6px;
  padding: 0.65rem 0.7rem;
}

.proj-month__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.4rem;
}

.proj-month__label {
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 0.78rem;
  color: var(--text-content);
  text-transform: capitalize;
}

.proj-month__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.proj-month__dot--green { background: #10b981; }
.proj-month__dot--amber { background: #f0a500; }
.proj-month__dot--red { background: #e84545; }

.proj-month__bar {
  display: flex;
  height: 6px;
  border-radius: 3px;
  overflow: hidden;
  background: #f1f5f9;
  margin-bottom: 0.35rem;
}

.proj-month__bar-segment--safe { background: #94a3b8; }
.proj-month__bar-segment--risk { background: #e84545; }

.proj-month__total {
  font-family: var(--font-mono);
  font-weight: 700;
  font-size: 1rem;
  color: var(--text-content);
}

.proj-month__detail {
  font-family: var(--font-body);
  font-size: 0.68rem;
  color: #64748b;
  margin-bottom: 0.5rem;
  margin-top: 0.1rem;
}

.proj-month__risks {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding-top: 0.4rem;
  border-top: 1px solid #f1f5f9;
}

.proj-month__no-risk {
  font-family: var(--font-body);
  font-size: 0.72rem;
  color: #94a3b8;
  font-style: italic;
}

.proj-month__risk-row {
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.proj-month__risk-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.proj-month__risk-dot--amber { background: #f0a500; }
.proj-month__risk-dot--red { background: #e84545; }

.proj-month__risk-concept {
  flex: 1;
  min-width: 0;
  font-family: var(--font-body);
  font-size: 0.7rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.proj-month__risk-amount {
  font-family: var(--font-mono);
  font-size: 0.7rem;
  color: var(--text-content);
  flex-shrink: 0;
}

.proj-month__risk-more {
  font-family: var(--font-body);
  font-size: 0.68rem;
  color: #94a3b8;
}

.proj-card__footer {
  display: flex;
  gap: 2rem;
  margin-top: 0.9rem;
  padding-top: 0.7rem;
  border-top: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.proj-card__footer-item {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.proj-card__footer-label {
  font-family: var(--font-body);
  font-size: 0.68rem;
  color: #94a3b8;
}

.proj-card__footer-value {
  font-family: var(--font-mono);
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--text-content);
}

.proj-card__footer-value--risk {
  color: #e84545;
}
</style>